import { ScrollType } from '@/Helper';
import { BaseColumns } from '@/model/CommonModel';
import { DragOutlined } from '@ant-design/icons';
import type { AutoScrollOptions, DragEndEvent, UniqueIdentifier } from '@dnd-kit/core';
import { DndContext } from '@dnd-kit/core';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { TablePaginationConfig } from 'antd';
import { Button, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { TableRowSelection } from 'antd/es/table/interface';
import React, { CSSProperties, useContext, useMemo } from 'react';

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
	'data-row-key': string;
}

interface RowContextProps {
	setActivatorNodeRef?: (element: HTMLElement | null) => void;
	listeners?: SyntheticListenerMap;
}

const RowContext = React.createContext<RowContextProps>({});

const Row: React.FC<RowProps> = props => {
	const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
		id: props['data-row-key'],
		animateLayoutChanges: () => false,
	});

	const style: React.CSSProperties = {
		...props.style,
		transform: CSS.Translate.toString(transform),
		transition,
		...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
	};

	const contextValue = useMemo<RowContextProps>(() => ({ setActivatorNodeRef, listeners }), [setActivatorNodeRef, listeners]);

	return (
		<RowContext.Provider value={contextValue}>
			<tr {...props} ref={setNodeRef} style={style} {...attributes} />
		</RowContext.Provider>
	);
};

const buttonStyles: CSSProperties = { cursor: 'move' };
const iconStyles: CSSProperties = { color: '#999999', fontSize: '19px' };
const DragHandle: React.FC = () => {
	const { setActivatorNodeRef, listeners } = useContext(RowContext);

	return <Button type='link' size='small' icon={<DragOutlined style={iconStyles} />} style={buttonStyles} ref={setActivatorNodeRef} {...listeners} />;
};

const autoScrollOptions: AutoScrollOptions = { threshold: { x: 0, y: 0.2 }, acceleration: 1 };

type Props<T> = {
	dataSource: T[];
	columns: ColumnsType<T>;
	onSortEnd: ({ oldIndex, newIndex }) => void;
	id?: string;
	className?: string;
	pagination?: false | TablePaginationConfig;
	rowSelection?: TableRowSelection<T>;
	scroll?: ScrollType;
	sortColumnKey?: string;
};

/**
 * Dev Notes:
 * this table uses index as id and it needs to be string to avoid issues
 * make sure all types T are typed on the component you are implementing this
 * the column with the sort icon should have a key parameter with value 'sort' or sortColumnKey provided
 */
const DraggableTable = <T extends BaseColumns>({
	id,
	className,
	pagination,
	dataSource,
	columns,
	rowSelection,
	scroll,
	onSortEnd,
	sortColumnKey = 'sort',
}: Props<T>) => {
	const columnsWithHandler = columns.map(x => {
		const column = x;
		if (x.key === sortColumnKey) {
			column.render = () => <DragHandle />;
			column.align = 'center';
		}

		return column;
	});

	const onDragEnd = ({ active, over }: DragEndEvent) => {
		if (active.id !== over?.id) {
			const newIndex = over?.id === null ? 0 : Number(over?.id);
			onSortEnd({ oldIndex: Number(active.id), newIndex });
		}
	};

	return (
		<DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd} autoScroll={autoScrollOptions}>
			<SortableContext items={dataSource.map(i => i.key as UniqueIdentifier)} strategy={verticalListSortingStrategy}>
				<Table<T>
					id={id}
					className={className}
					components={{
						body: { row: Row },
					}}
					rowKey='key'
					size='small'
					columns={columnsWithHandler}
					dataSource={dataSource}
					pagination={pagination}
					rowSelection={rowSelection}
					scroll={scroll}
				/>
			</SortableContext>
		</DndContext>
	);
};

export { DraggableTable };
